import { HttpClient, HttpDownloadProgressEvent, HttpEvent, HttpEventType, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { filter, map, Observable } from 'rxjs';
import { ChatApiResponse } from '../models/chat-response';
import { URLS } from '../../environments/environment';
import { TranslateService } from '@ngx-translate/core';


export interface UiRespuesta {
  tipo: 'seleccion' | 'texto';
  texto: string;
  opciones?: string[];
}


@Injectable({
  providedIn: 'root'
})

export class ChatbotService {
  private http = inject(HttpClient);
  private translate = inject(TranslateService);
  private apiUrl = URLS.API + URLS.chatbot;

  private getIdioma(): string {
    return this.translate.currentLang === 'en' ? 'ing' : 'esp';
  }

  private getOrCreateUserId(): string {
    const key = 'chat_user_id';
    let uid = sessionStorage.getItem(key);
    if (!uid) {
      uid = `user_${Date.now()}`;
      sessionStorage.setItem(key, uid);
    }
    return `user_${Date.now()}`;
  }

  enviarConsulta(consulta: string): Observable<UiRespuesta> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });
    const body = { consulta, user_id: this.getOrCreateUserId(), idioma: this.getIdioma() };
    return this.http.post<ChatApiResponse>(this.apiUrl, body, { headers }).pipe(
      map((resp) => {
        console.log(resp)
        const msg = (resp as any).message;

        if ((msg?.type === 'seleccion_categoria' || msg?.type === 'seleccion_entidad') && typeof msg.content === 'string') {
          try {
            const parsed = JSON.parse(msg.content) as { texto: string; opciones: string[] };
            return { tipo: 'seleccion', texto: parsed.texto, opciones: parsed.opciones } as UiRespuesta;
          } catch {
            return { tipo: 'texto', texto: msg.content ?? '' } as UiRespuesta;
          }
        }
        return { tipo: 'texto', texto: msg?.content ?? '' } as UiRespuesta;
      })
    );
  }


  enviarConsultaStream(consulta: string): Observable<{
    texto: string;
    opciones?: string[];
    done: boolean;
  }> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    const body = { consulta, user_id: this.getOrCreateUserId(), idioma: this.getIdioma() };

    return this.http.post(this.apiUrl, body, {
      headers,
      observe: 'events',
      responseType: 'text',
      reportProgress: true
    }).pipe(
      map((event: HttpEvent<string>) => {
        if (event.type === HttpEventType.DownloadProgress) {
          const partialText = (event as HttpDownloadProgressEvent).partialText || '';
          return this.parseResponse(partialText);
        } else if (event.type === HttpEventType.Response) {
          return this.parseResponse(event.body || '');
        }
        return null;
      }),
      filter((result): result is { texto: string; opciones?: string[]; done: boolean } =>
        result !== null && result.texto.length > 0
      )
    );
  }

  /**
   * Detecta y parsea ambos tipos de respuesta
   */
  private parseResponse(rawText: string): {
    texto: string;
    opciones?: string[];
    done: boolean;
  } | null {
    if (!rawText.trim()) return null;

    const lines = rawText.trim().split('\n');

    // Intentar detectar si es respuesta con opciones (un solo chunk)
    if (lines.length === 1) {
      try {
        const response = JSON.parse(lines[0]);

        // Detectar respuesta con opciones
        if (response.message?.type === 'seleccion_entidad' || response.message?.type === 'seleccion_categoria') {
          const contentParsed = JSON.parse(response.message.content);
          return {
            texto: contentParsed.texto,
            opciones: contentParsed.opciones,
            done: true
          };
        }
      } catch (e) {
        // No es selección, continuar con parsing de stream
      }
    }

    // Parsear como stream (múltiples chunks)
    let fullText = '';
    let isDone = false;

    for (const line of lines) {
      if (!line.trim()) continue;

      try {
        const chunk = JSON.parse(line);

        // Acumular contenido del stream
        if (chunk.message?.content) {
          fullText += chunk.message.content;
        }

        // Verificar si terminó
        if (chunk.done === true) {
          isDone = true;
          break;
        }
      } catch (e) {
        console.warn('Error parseando chunk:', line);
      }
    }

    if (fullText.length > 0) {
      return {
        texto: fullText,
        done: isDone
      };
    }

    return null;
  }



}
